import React from 'react';
import { read_client_lifecycle_label } from '../../../../utils/client_lifecycle';
import {
    Builder_,
    Checkbox_,
    Dropdown_,
    Main_Plain,
    Slider_
} from 'monica-alexandria';
import styled from 'styled-components';

const BrandsFiltersContent = styled.div`
    display: grid;
    gap: 2rem;
`;

const BrandsInfoFilters = styled.div`
    display: grid;
    gap: 1.2rem;
`;

export const brandInfoOptions = [
    { key: 'brand', label: 'Brand name' },
    { key: 'category', label: 'Category' },
    { key: 'subcategory', label: 'Subcategory' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'location', label: 'Location' }
];

export const brandAutoTagOptions = [
    { key: 'questad_paid', label: 'Paid Questad' },
    { key: 'questad_lost', label: 'Lost Questad' }
];

export const questClientStatusOptions = [
    { key: 'active', label: 'Active Quest associations' },
    { key: 'past', label: 'Past Quest associations' }
];

export const missingBrandFilterValue = '__missing__';

export const defaultBrandFilters = {
    brandStatuses: [],
    category: '',
    subcategory: '',
    infoHas: [],
    infoMissing: [],
    brandAutoTags: [],
    questClientStatuses: []
};

const read_unique_values = (values = []) => {
    return [...new Set((values || []).map((value) => `${value || ''}`.trim()).filter(Boolean))];
};

/**
 * Returns detached, API-safe Brand filters while keeping Has/Missing mutually exclusive.
 */
export const read_brand_filters = (filters = {}) => {
    const allowedInfoFields = new Set(brandInfoOptions.map((option) => option.key));
    const allowedAutoTags = new Set(brandAutoTagOptions.map((option) => option.key));
    const allowedQuestClientStatuses = new Set(questClientStatusOptions.map((option) => option.key));
    const infoHas = read_unique_values(filters?.infoHas).filter((field) => allowedInfoFields.has(field));
    const infoMissing = read_unique_values(filters?.infoMissing).filter((field) => {
        return allowedInfoFields.has(field) && !infoHas.includes(field);
    });

    return {
        brandStatuses: read_unique_values((filters?.brandStatuses || []).map(read_client_lifecycle_label)),
        category: `${filters?.category || ''}`.trim(),
        subcategory: `${filters?.subcategory || ''}`.trim(),
        infoHas,
        infoMissing,
        brandAutoTags: read_unique_values(filters?.brandAutoTags).filter((tag) => allowedAutoTags.has(tag)),
        questClientStatuses: read_unique_values(filters?.questClientStatuses).filter((status) => {
            return allowedQuestClientStatuses.has(status);
        })
    };
};

/**
 * Adds or removes one multi-select value without mutating the current filter draft.
 */
export const read_toggled_brand_filter_value = (values, selectedValue) => {
    const currentValues = [...(values || [])];

    return currentValues.includes(selectedValue)
        ? currentValues.filter((value) => value !== selectedValue)
        : [...currentValues, selectedValue];
};

/**
 * Sets one Info field to All, Has, or Missing from its dropdown.
 */
export const read_brand_info_filter_change = (filters, field, mode) => {
    const normalizedFilters = read_brand_filters(filters);
    const nextInfoHas = normalizedFilters.infoHas.filter((value) => value !== field);
    const nextInfoMissing = normalizedFilters.infoMissing.filter((value) => value !== field);

    return read_brand_filters({
        ...normalizedFilters,
        infoHas: mode === 'has' ? [...nextInfoHas, field] : nextInfoHas,
        infoMissing: mode === 'missing' ? [...nextInfoMissing, field] : nextInfoMissing
    });
};

/**
 * Counts every applied Brand filter selection, excluding the separate date sort control.
 */
export const read_active_brand_filters_count = (filters) => {
    const normalizedFilters = read_brand_filters(filters);

    return normalizedFilters.brandStatuses.length
        + Number(Boolean(normalizedFilters.category))
        + Number(Boolean(normalizedFilters.subcategory))
        + normalizedFilters.infoHas.length
        + normalizedFilters.infoMissing.length
        + normalizedFilters.brandAutoTags.length
        + normalizedFilters.questClientStatuses.length;
};

/**
 * Renders draft Brand filters that are committed only when Apply is clicked.
 */
export const Brands_Filters_Slider = (props) => {
    const t = props?.t || ((value) => value);
    const filters = read_brand_filters(props?.filters);
    const statusOptions = read_unique_values(props?.statusOptions);
    const categoryOptions = read_unique_values(props?.categoryOptions);
    const subcategoryOptions = read_unique_values(props?.subcategoryOptions);
    const change_filters = (nextFilters) => props?.onFiltersChange?.(read_brand_filters(nextFilters));

    return (
        <Slider_
            title={t('Filters')}
            text={t('Apply')}
            onClick={props?.onApply}
            onClose={props?.onClose}
        >
            <BrandsFiltersContent>
                <Builder_ title={t('Sort')}>
                    <Dropdown_ label={t('Created date')}>
                        <select
                            aria-label={t('Created date')}
                            value={props?.sortOrder}
                            onChange={(event) => props?.onSortOrderChange(event.target.value)}
                        >
                            <option value="newest">{t('Newer first')}</option>
                            <option value="oldest">{t('Older first')}</option>
                        </select>
                    </Dropdown_>
                </Builder_>
                <Builder_ title={t('Last status')}>
                    {statusOptions.map((status) => (
                        <Checkbox_
                            key={status}
                            text={t(status)}
                            checked={filters.brandStatuses.includes(status)}
                            onClick={() => change_filters({
                                ...filters,
                                brandStatuses: read_toggled_brand_filter_value(filters.brandStatuses, status)
                            })}
                        />
                    ))}
                </Builder_>
                <Builder_>
                    <Dropdown_ label={t('Category')}>
                        <select
                            aria-label={t('Category')}
                            value={filters.category}
                            onChange={(event) => change_filters({
                                ...filters,
                                category: event.target.value
                            })}
                        >
                            <option value="">{t('All categories')}</option>
                            <option value={missingBrandFilterValue}>{t('No category')}</option>
                            {categoryOptions.map((category) => (
                                <option key={category} value={category}>{t(category)}</option>
                            ))}
                        </select>
                    </Dropdown_>
                </Builder_>
                <Builder_>
                    <Dropdown_ label={t('Subcategory')}>
                        <select
                            aria-label={t('Subcategory')}
                            value={filters.subcategory}
                            onChange={(event) => change_filters({
                                ...filters,
                                subcategory: event.target.value
                            })}
                        >
                            <option value="">{t('All subcategories')}</option>
                            <option value={missingBrandFilterValue}>{t('No subcategory')}</option>
                            {subcategoryOptions.map((subcategory) => (
                                <option key={subcategory} value={subcategory}>{t(subcategory)}</option>
                            ))}
                        </select>
                    </Dropdown_>
                </Builder_>
                <Builder_ title={t('Info')}>
                    <BrandsInfoFilters>
                        {brandInfoOptions.map((option) => (
                            <Dropdown_ key={option.key} label={t(option.label)}>
                                <select
                                    aria-label={t(option.label)}
                                    value={filters.infoHas.includes(option.key)
                                        ? 'has'
                                        : filters.infoMissing.includes(option.key) ? 'missing' : 'all'}
                                    onChange={(event) => change_filters(read_brand_info_filter_change(
                                        filters,
                                        option.key,
                                        event.target.value
                                    ))}
                                >
                                    <option value="all">{t('All')}</option>
                                    <option value="has">{t('Has')}</option>
                                    <option value="missing">{t('Missing')}</option>
                                </select>
                            </Dropdown_>
                        ))}
                    </BrandsInfoFilters>
                </Builder_>
                <Builder_ title={t('Quest history')}>
                    {questClientStatusOptions.map((status) => (
                        <Checkbox_
                            key={status.key}
                            text={t(status.label)}
                            checked={filters.questClientStatuses.includes(status.key)}
                            onClick={() => change_filters({
                                ...filters,
                                questClientStatuses: read_toggled_brand_filter_value(
                                    filters.questClientStatuses,
                                    status.key
                                )
                            })}
                        />
                    ))}
                </Builder_>
                <Builder_ title={t('Auto-tags')}>
                    {brandAutoTagOptions.map((tag) => (
                        <Checkbox_
                            key={tag.key}
                            text={t(tag.label)}
                            checked={filters.brandAutoTags.includes(tag.key)}
                            onClick={() => change_filters({
                                ...filters,
                                brandAutoTags: read_toggled_brand_filter_value(filters.brandAutoTags, tag.key)
                            })}
                        />
                    ))}
                </Builder_>
                {read_active_brand_filters_count(props?.appliedFilters) || read_active_brand_filters_count(filters) || props?.sortOrder !== 'newest' ? (
                    <Main_Plain text={t('Clear filters')} onClick={props?.onClear} />
                ) : null}
            </BrandsFiltersContent>
        </Slider_>
    );
};
